// OncoDiary Caregiver Quiz - Complete 23-Page Flow
// Original content adapted for cancer caregivers & supporters
// Stage: Relationship to patient (Still supporting through treatment, Recently finished, Long-term support, etc.)

export const CAREGIVER_QUIZ_DATA = {
  metadata: {
    audience: "caregiver",
    totalPages: 32,
    title: "OncoDiary Support Quiz - For Cancer Caregivers",
    description: "Discover the right OncoDiary resources for your caregiving journey",
    timeToComplete: "3-4 minutes"
  },

  home: {
    pageNum: 0,
    type: "informational",
    headline: "You're supporting them. Who's supporting you?",
    subheading: "Loving someone through cancer is beautiful and exhausting. You deserve care and tools too—not just them.",
    benefits: [
      "Understand what they're really going through (and why it matters)",
      "Learn caregiving strategies that actually work",
      "Protect your own emotional and physical health",
      "Build community with others who understand"
    ],
    cta: "Let's find your path forward",
    ctaText: "Continue",
    nextPage: "page0_gender"
  },

  page0_gender: {
    pageNum: 1,
    type: "question",
    singleSelect: true,
    question: "How do you identify?",
    emoji: "👤",
    options: [
      { label: "👩 Female", value: "female_caregiver" },
      { label: "👨 Male", value: "male_caregiver" },
      { label: "🌈 Non-binary / Prefer not to say", value: "other_caregiver" }
    ],
    nextPage: "page0_age"
  },

  page0_age: {
    pageNum: 2,
    type: "question",
    singleSelect: true,
    question: "How old are you?",
    emoji: "🤔",
    options: [
      { label: "18-29", value: "18-29" },
      { label: "30-39", value: "30-39" },
      { label: "40-49", value: "40-49" },
      { label: "50-59", value: "50-59" },
      { label: "60-69", value: "60-69" },
      { label: "70+", value: "70+" }
    ],
    nextPage: "page1_caregiver_stage"
  },

  page1_caregiver_stage: {
    pageNum: 3,
    type: "question",
    singleSelect: true,
    question: "Where are they in their cancer journey?",
    emoji: "📍",
    options: [
      { label: "🩺 Still in active treatment", value: "active_treatment" },
      { label: "✅ Just finished treatment (0-6 months ago)", value: "recent_finish" },
      { label: "📅 1-2 years post-treatment", value: "1-2_years" },
      { label: "⏳ 3+ years post-treatment", value: "3plus_years" }
    ],
    nextPage: "page2_social_proof"
  },

  page2_social_proof: {
    pageNum: 4,
    type: "informational",
    singleSelect: false,
    heading: "You're Not Alone in This Journey",
    emoji: "💙",
    contentByGender: {
      female_caregiver: {
        message: "Many women are in your shoes right now. Thousands of female caregivers know the weight of what you're carrying. Research shows that supporting someone through cancer impacts your emotional well-being just as deeply as theirs.\n\nYou're not weak for struggling. You're human. And you deserve support too. That's why this was designed by psycho-oncologist Ellie Urban—to help you heal while you care.",
        image: "/images/placeholder-women-caregivers.webp"
      },
      male_caregiver: {
        message: "Many men are in your shoes right now. Male caregivers often experience the same emotional toll as women—but face unique pressure to 'be strong.' Research shows your feelings matter just as much.\n\nYour struggle is real. Your support is valid. And you deserve tools to help you through this. That's why this was designed by psycho-oncologist Ellie Urban—grounded in caregiver research and trusted by caregivers like you.",
        image: "/images/placeholder-men-caregivers.webp"
      }
    },
    nextPage: "page3_primary_concern"
  },

  page3_primary_concern: {
    pageNum: 5,
    type: "question",
    singleSelect: false,
    question: "What's your biggest struggle right now as a caregiver?",
    emoji: "💔",
    options: [
      { label: "😰 Constant anxiety about their health or prognosis", value: "anxiety" },
      { label: "😴 Sleep problems from worry and stress", value: "insomnia" },
      { label: "😞 Watching them suffer and feeling helpless", value: "helplessness" },
      { label: "😕 Feeling invisible—everyone asks about them, not me", value: "invisible" },
      { label: "😫 Grief of who they were before cancer", value: "grief" },
      { label: "🧠 Brain fog, forgetfulness from caregiver burnout", value: "brain_fog" },
      { label: "🆘 All of the above—I'm drowning", value: "all_above" }
    ],
    nextPage: "page4_daily_impact"
  },

  page4_daily_impact: {
    pageNum: 6,
    type: "question",
    singleSelect: false,
    question: "How is caregiving affecting your day-to-day life?",
    emoji: "⚡",
    options: [
      { label: "💼 Struggling to keep up at work or school", value: "work" },
      { label: "👨‍👩‍👧 Missing time with other family members or kids", value: "family" },
      { label: "🌙 My sleep is completely disrupted", value: "sleep" },
      { label: "😔 Feeling isolated—no time to see friends", value: "isolation" },
      { label: "🎨 Lost interest in hobbies I used to love", value: "hobbies" },
      { label: "😰 Intrusive thoughts about worst-case scenarios", value: "intrusive_thoughts" },
      { label: "📋 Just trying to manage day-to-day logistics", value: "logistics" }
    ],
    nextPage: "page5_how_long"
  },

  page5_how_long: {
    pageNum: 7,
    type: "question",
    singleSelect: true,
    question: "How long have you been their primary caregiver?",
    emoji: "⏱️",
    options: [
      { label: "🆕 Just started—still in diagnosis/early treatment", value: "just_started" },
      { label: "⏱️ 0-6 months of active caregiving", value: "0-6_months" },
      { label: "📅 1-2 years of ongoing support", value: "1-2_years" },
      { label: "♾️ 3+ years—this is our long-term reality", value: "3plus_years" }
    ],
    nextPage: "page6_severity"
  },

  page6_severity: {
    pageNum: 8,
    type: "question",
    singleSelect: true,
    question: "On a scale, how would you describe your caregiver burnout?",
    emoji: "🌡️",
    options: [
      { label: "🚨 Crisis—I'm at a breaking point", value: "crisis" },
      { label: "⚠️ Severe—Caregiving is overwhelming my life", value: "severe" },
      { label: "🟡 Moderate—Hard, but I'm managing", value: "moderate" },
      { label: "🟢 Mild—Challenging, but I'm okay", value: "mild" }
    ],
    nextPage: "page6_self_care_tools"
  },

  page6_self_care_tools: {
    pageNum: 9,
    type: "question",
    singleSelect: false,
    question: "What tools or practices are you currently using for self-care or emotional support?",
    emoji: "🌱",
    subtext: "Choose all that apply",
    options: [
      { label: "🧘 Meditation or mindfulness", value: "meditation" },
      { label: "📝 Journaling or writing", value: "journaling" },
      { label: "💬 Talking to friends/family", value: "talking_friends_family" },
      { label: "🛋️ Therapy or counseling", value: "therapy" },
      { label: "👥 Support groups", value: "support_groups" },
      { label: "🏃 Movement (yoga, walking, exercise)", value: "movement" },
      { label: "🌬️ Breathing exercises", value: "breathing" },
      { label: "📚 Reading/learning about cancer", value: "reading_learning" },
      { label: "🙏 Spiritual or faith practices", value: "spiritual_faith" },
      { label: "❌ I'm not using any tools right now", value: "none" },
      { label: "❓ Other", value: "other" }
    ],
    nextPage: "page7_holistic_sleep"
  },

  // PAGE 7: HOLISTIC ASSESSMENT — SLEEP (Caregiver)
  page7_holistic_sleep: {
    pageNum: 10,
    type: "question",
    singleSelect: true,
    question: "How's your sleep quality as a caregiver?",
    subtext: "Caregiver sleep deprivation increases burnout by 60%. Sleep is foundational to everything.",
    options: [
      { label: "😴 I sleep 7-9 hours and feel rested", value: "good_sleep" },
      { label: "😴 I sleep 5-7 hours but feel tired", value: "moderate_sleep" },
      { label: "😵 I get less than 5 hours (on alert)", value: "poor_sleep" },
      { label: "🌙 My sleep is completely disrupted", value: "no_sleep" }
    ],
    nextPage: "page8_holistic_movement",
    research: "Sleep Foundation, Caregiver Action Network (2023)"
  },

  // PAGE 8: HOLISTIC ASSESSMENT — MOVEMENT (Caregiver)
  page8_holistic_movement: {
    pageNum: 11,
    type: "question",
    singleSelect: true,
    question: "How much are you moving and taking care of your own body?",
    subtext: "Caregivers who exercise reduce stress by 45% and have better emotional resilience.",
    options: [
      { label: "🏃 Regular movement despite caregiving demands", value: "active" },
      { label: "🚶 Some movement, but sporadic", value: "light_activity" },
      { label: "🪑 Rarely move, caregiving takes all my time", value: "sedentary" },
      { label: "😫 Completely sedentary due to caregiving", value: "no_movement" }
    ],
    nextPage: "page9_holistic_nutrition",
    research: "American Journal of Preventive Medicine, Caregiver Health (2023)"
  },

  // PAGE 9: HOLISTIC ASSESSMENT — NUTRITION (Caregiver)
  page9_holistic_nutrition: {
    pageNum: 12,
    type: "question",
    singleSelect: true,
    question: "How are you feeding and hydrating yourself?",
    subtext: "Proper nutrition directly impacts caregiver mood, patience, and ability to support.",
    options: [
      { label: "🍽️ Regular meals, staying hydrated", value: "good_nutrition" },
      { label: "⏭️ Eat irregularly, sometimes forget meals", value: "inconsistent" },
      { label: "🍔 Surviving on quick food, minimal water", value: "poor_nutrition" },
      { label: "😭 Food is lowest priority, barely eating", value: "neglecting" }
    ],
    nextPage: "page10_holistic_stress",
    research: "Nutrition Reviews, Caregiver Wellness Studies (2023)"
  },

  // PAGE 10: HOLISTIC ASSESSMENT — ROUTINE & SUPPORT (Caregiver)
  page10_holistic_stress: {
    pageNum: 13,
    type: "question",
    singleSelect: true,
    question: "Do you have personal time and support for yourself?",
    subtext: "Caregivers with personal time and support systems report 50% less burnout.",
    options: [
      { label: "💚 I have regular breaks and support system", value: "good_support" },
      { label: "🤝 I get some breaks, some support", value: "partial_support" },
      { label: "😔 I rarely get breaks, feeling isolated", value: "minimal_support" },
      { label: "😞 No breaks, no support, completely alone", value: "no_support" }
    ],
    nextPage: "page11_holistic_integration",
    research: "JAMA Internal Medicine, Caregiver Burnout (2023)"
  },

  // PAGE 11: HOW IT ALL CONNECTS — HOLISTIC RECOVERY (Caregiver)
  page11_holistic_integration: {
    pageNum: 14,
    type: "informational",
    heading: "Taking care of yourself is taking care of them",
    image: "/images/placeholder-how-it-works.webp",
    message: `You just told me about four foundations of your wellness: sleep, movement, nutrition, and support.

Here's what research shows: **these aren't separate things. They work together.**

When you **sleep better**, your nervous system calms down. You're more patient, clearer thinking, less reactive.

When you **move your body**—even just a walk—your brain releases the chemicals that fight burnout. Your mood improves. You have more to give.

When you **eat and drink consistently**, your energy stabilizes. You think clearer. You handle stress better. You're more present.

When you **have support and build routine**, your body knows what to expect. Your nervous system relaxes. Everything becomes sustainable.

**They're all connected. Each one makes the others work better.**

This is why so many caregivers collapse: you can access all the support groups in the world, but if you're not sleeping, moving, eating, and building stability, you burn out anyway.

**Your recovery as a caregiver needs BOTH:**
• Emotional support (processing caregiver trauma, connection, validation)
• Physical wellness (sleep tracking, movement, nutrition, support systems)

The app you're about to explore tracks both. Because real sustainability happens when you address both.

You're not weak for struggling. Your nervous system just needs tools to remember that you matter too.`,
    ctaText: "Continue",
    nextPage: "page7_education_1"
  },

  page7_education_1: {
    pageNum: 15,
    type: "informational",
    heading: "The Caregiver Paradox: Why the Struggle Doesn't End When Treatment Does",
    emoji: "🧠",
    image: "/images/placeholder-brain-nervous-system.webp",
    progressIndicator: "1 of 3",
    content: "When treatment ends, everyone celebrates. Cards arrive. Calls come in. 'It's over now.'\n\nBut you know it isn't.\n\nDuring months of scans, surgeries, hospital corridors, and middle-of-the-night emergencies, your nervous system learned one job: stay alert. Watch for the next bad sign. Be ready for anything.\n\n**That training doesn't switch off when treatment ends.** Your body doesn't know the crisis is over — because for you, in many ways, it isn't.\n\nYou're still listening for changes in their breathing. Still bracing every time the phone rings. Still scanning their face for signs something's wrong.\n\n**This is caregiver trauma.** It's a real, physiological response — not a character flaw, and not something you can simply decide to stop feeling.\n\nThe medical system is built to treat the patient. Almost no one is trained to help the person standing beside them recover from what caregiving does to a nervous system.\n\nThat's the gap. And it's why the struggle doesn't end when treatment does — for either of you.",
    nextPage: "page8_education_2"
  },

  page8_education_2: {
    pageNum: 16,
    type: "informational",
    heading: "The Invisible Burden: Why Your Struggle Often Goes Unseen",
    emoji: "😔",
    progressIndicator: "2 of 3",
    content: "Everyone asks how they're doing. Almost no one asks how you're doing.\n\nIf you're a woman in this role, you've likely felt the quiet expectation: stay nurturing, stay composed, hold everyone together — even while you're falling apart inside. There's no script for 'I can't do this today' when someone else is fighting for their life.\n\nIf you're a man in this role, you've likely felt a different pressure: be strong, fix it, manage it. But cancer isn't a problem you can fix. And carrying that pressure silently, with nowhere to put it, takes its own toll.\n\nEither way, the message lands the same: **your needs come second. Your exhaustion doesn't count. Your fear isn't the point.**\n\nSo you stop mentioning it. You become the dependable one — the person everyone else leans on, with no one underneath to catch you.\n\nOver time, this invisible weight changes you. Sleep gets harder. Patience runs thin. You start to lose track of who you were before all of this became your life.\n\nThis isn't an exaggeration, and it isn't self-pity. It's what sustained, unacknowledged caregiving does to a person.\n\n**Your burden is real. It deserves to be seen — and it deserves its own path to healing.**",
    nextPage: "page9_what_tried"
  },

  page9_what_tried: {
    pageNum: 17,
    type: "question",
    singleSelect: false,
    question: "What have you tried to cope with caregiver stress?",
    emoji: "🔍",
    options: [
      { label: "🛋️ Therapy or counseling (but it doesn't 'get' caregiving)", value: "therapy" },
      { label: "💊 Anxiety medication or sleep aids", value: "medication" },
      { label: "📚 Books or self-help about caregiving", value: "books" },
      { label: "🧘 Meditation or mindfulness (hard to focus)", value: "meditation" },
      { label: "👥 Caregiver support groups (hit or miss)", value: "support_groups" },
      { label: "📝 Journaling or venting to friends", value: "journaling" },
      { label: "😔 Nothing—I just push through and hope it gets better", value: "nothing" }
    ],
    nextPage: "page10_why_nothing_worked_1"
  },

  page10_why_nothing_worked_1: {
    pageNum: 18,
    type: "informational",
    heading: "Why Generic Support Falls Short for Caregivers",
    emoji: "📋",
    image: "/images/placeholder-failed-solutions.webp",
    progressIndicator: "1 of 2",
    content: "You may have tried therapy, support groups, books, even medication. And maybe they helped a little. But something was still missing.\n\nHere's why:\n\n**Most therapists aren't trained in caregiver trauma specifically.** They can treat anxiety in general — but they don't know what it's like to sit in a waiting room for six hours, or to watch someone you love disappear into illness while you hold the family together.\n\n**Support groups often stop at venting.** They can make you feel less alone for an hour — but you leave with the same nervous system, the same patterns, and no framework for what to do next.\n\n**Medication can quiet the symptoms but not the cause.** Your nervous system learned to stay on high alert. That's not something a prescription rewires on its own.\n\n**'Just take time for yourself' misses the point entirely.** You can't relax into a bubble bath when part of you is still listening for footsteps, still on call.\n\n**What caregivers actually need is trained guidance** that understands this experience specifically — and a path designed around one core truth: **caregiver trauma rewires you, so recovery has to work the same way.**",
    nextPage: "page11_why_nothing_worked_2"
  },

  page11_why_nothing_worked_2: {
    pageNum: 19,
    type: "informational",
    heading: "What DOES Work for Cancer Caregivers",
    emoji: "✨",
    image: "/images/placeholder-healing-path.webp",
    progressIndicator: "2 of 2",
    content: "Caregivers recover when they have:\n\n1. **Permission to prioritize their own healing** — Not as selfish. As essential. Your loved one needs you healthy.\n\n2. **A framework for understanding caregiver trauma** — Knowing your nervous system responses are *normal* stops you from judging yourself as weak.\n\n3. **Practical tools to rebuild identity** — Not 'self-care bubble baths.' Real practices that reconnect you to who you are beyond caregiving.\n\n4. **Community that gets it** — Not pity. Connection with others who've walked this exact path.\n\n5. **A pathway from burnout to presence** — From hypervigilance to groundedness. From resentment to sustainable support.\n\n6. **Realistic milestones** — Small wins: sleeping through the night. Laughing again. Having one conversation not about cancer. These matter.\n\nThis is what real recovery looks like for caregivers. Not pretending caregiving isn't hard. But learning how to carry the weight without letting it break you.",
    nextPage: "page12_intro_oncodiary_1"
  },

  page12_intro_oncodiary_1: {
    pageNum: 20,
    type: "informational",
    heading: "Introducing OncoDiary: Built for the Caregiver's Journey",
    emoji: "📱",
    progressIndicator: "1 of 3",
    content: "OncoDiary isn't about managing the patient's treatment. It's about *your* recovery as a caregiver.\n\nWe offer three interconnected products designed specifically for caregivers recovering from the trauma of cancer support:\n\n**1. 'Through The Storm' — The Caregiver's Companion Book**\nA grounded, evidence-based guide to understanding caregiver trauma, rebuilding identity, and creating sustainable support. Written by psycho-oncologists and caregivers who've been where you are. Not toxic positivity. Real tools.\n\n**2. OncoDiary App**\nA daily practice space. Reflection prompts designed for caregivers. Nervous system regulation techniques (not meditation fluff). Tracking your own healing alongside supporting them. A private space that's just for you.\n\n**3. Wellness Digest**\nMonthly delivered journal & insights. Curated articles from leading caregiving experts. Monthly themes: Setting boundaries, Rebuilding identity, Managing anticipatory grief, Reconnecting to joy. Shows up in your mailbox—something just for you.\n\nTogether, they create a complete recovery system for caregivers.",
    nextPage: "page13_how_it_works"
  },

  page13_how_it_works: {
    pageNum: 21,
    type: "informational",
    heading: "How Caregiver Recovery Works: The 6-Step Process",
    emoji: "🔄",
    progressIndicator: "2 of 3",
    content: "OncoDiary guides caregivers through a proven recovery arc:\n\n**Step 1: Acknowledge**\nAdmit caregiving has changed you. Name what you're experiencing. (Not weakness—clarity.)\n\n**Step 2: Understand**\nLearn how caregiving trauma affects the nervous system. Why you react the way you do. Why guilt, burnout, and isolation are predictable outcomes.\n\n**Step 3: Grieve**\nAllow yourself to mourn: The life you had before caregiving. The relationship as it was. The person you were. This isn't depression—it's necessary.\n\n**Step 4: Rebuild**\nReclaim pieces of your identity outside caregiving. Sleep. Relationships. Interests. Joy. Small acts of defiance against the narrowing.\n\n**Step 5: Integrate**\nLive as both caregiver and person. Set boundaries. Show up for them from a grounded place, not a desperate one.\n\n**Step 6: Thrive**\nMove from 'surviving caregiving' to 'living while caregiving.' It's possible.\n\nThis isn't linear. You'll cycle through these. And that's normal.",
    nextPage: "page14_what_to_expect"
  },

  page14_what_to_expect: {
    pageNum: 22,
    type: "informational",
    heading: "What You Can Expect on Your Caregiver Recovery Timeline",
    emoji: "📅",
    progressIndicator: "3 of 3",
    content: "**Day 1-3 (Just Started)**\nYou feel seen. Someone finally gets it. You're not broken—you're experiencing a predictable trauma response. Relief mixed with grief about lost time.\n\n**Week 1-3 (First Shift)**\nYou sleep a bit better. You notice small moments of peace. You laugh at something. These feel weird, guilty—but they're signs your nervous system is beginning to relax.\n\n**Week 4+ (Identity Returning)**\nYou remember something you loved before caregiving. You set a boundary with someone. You ask for help without apologizing. You see yourself as more than 'caregiver.'\n\n**Month 2-3 (Sustainable Pattern)**\nYou're showing up for them from a grounded place, not desperation. You have practices that work. You're sleeping, laughing, living. Caregiving is still hard, but it's not consuming you.\n\n**Month 4+ (Thriving)**\nYou've built a life that includes caregiving—not one that's defined by it. You're healed enough to hold joy and grief at the same time. You're modeling recovery for them. And that's powerful.\n\nEvery caregiver's timeline is different. But the pattern is real: Recovery is possible.",
    nextPage: "page15_results_loading"
  },

  page15_results_loading: {
    pageNum: 23,
    type: "informational",
    heading: "Calculating Your Caregiver Recovery Profile",
    emoji: "⏳",
    isLoadingPage: true,
    loadingSteps: [
      "Analyzing your answers...",
      "Assessing caregiver burden patterns...",
      "Identifying recovery priorities...",
      "Building your personalized path..."
    ],
    nextPage: "page16_results_summary"
  },

  page16_results_summary: {
    pageNum: 24,
    type: "informational",
    heading: "Your Caregiver Assessment",
    emoji: "📊",
    contentByGender: {
      female_caregiver: {
        message: "Before recovery, caregiver burnout looks like:\n• Emotional Toll: 8.2/10\n• Identity Loss: 7.6/10\n• Social Isolation: 8.1/10\n• Nervous System Dysregulation: 7.9/10\n\nAfter 60 days of consistent OncoDiary practice, caregivers report significant improvements in sleep, emotional presence, and sense of self.",
        image: "/images/placeholder-woman-caregiver-recovery.webp"
      },
      male_caregiver: {
        message: "Before recovery, caregiving trauma manifests as:\n• Suppressed Anxiety: 8.0/10\n• Identity Erosion: 7.8/10\n• Relationship Strain: 8.3/10\n• Nervous System Overload: 8.1/10\n\nAfter implementing OncoDiary practices, men caregivers experience more stability, presence, and reconnection with themselves.",
        image: "/images/placeholder-man-caregiver-recovery.webp"
      }
    },
    nextPage: "page17_conclusions"
  },

  page17_conclusions: {
    pageNum: 25,
    type: "informational",
    heading: "What Your Results Mean",
    emoji: "💡",
    content: "Your responses show that caregiving has fundamentally changed your nervous system and identity. This isn't weakness. This is the predictable outcome of sustained caregiving trauma.\n\nThe good news: This is reversible.\n\nCaregivers who engage with a structured recovery program (like OncoDiary) see measurable improvements in sleep, emotional stability, relationship quality, and sense of self within weeks.\n\nBut it requires three things:\n1. **Understanding** — Why you react the way you do\n2. **Permission** — To prioritize your own healing\n3. **Consistency** — Daily practices that rewire your nervous system\n\nOncoDiary provides all three. The question is: Are you ready to commit to your own recovery?",
    nextPage: "page18_projection"
  },

  page18_projection: {
    pageNum: 26,
    type: "question",
    singleSelect: false,
    question: "If you finally recovered from caregiver burnout, what would change most for you?",
    emoji: "✨",
    options: [
      { label: "😴 I'd sleep through the night without anxiety waking me", value: "sleep" },
      { label: "🆔 I'd remember who I am outside of caregiving", value: "identity" },
      { label: "💕 I'd have more patience and presence with my loved one", value: "relationships" },
      { label: "🎯 I could focus on work/life without constant worry", value: "work" },
      { label: "🧠 I'd stop the catastrophic thinking and doom spiraling", value: "thoughts" },
      { label: "☮️ I'd feel peace—even just moments of it", value: "peace" },
      { label: "💚 All of it—I want my life back", value: "all" }
    ],
    nextPage: "page20_loading_eligibility"
  },

  page20_loading_eligibility: {
    pageNum: 27,
    type: "informational",
    heading: "Creating Your Personalized Caregiver Recovery Plan",
    emoji: "🎯",
    isLoadingPage: true,
    loadingSteps: [
      "Reviewing your caregiver profile...",
      "Personalizing recovery pathways...",
      "Preparing your welcome package..."
    ],
    nextPage: "page21_eligibility"
  },

  page21_eligibility: {
    pageNum: 28,
    type: "informational",
    heading: "You're Ready for Recovery",
    emoji: "💪",
    contentByGender: {
      female_caregiver: {
        message: "Based on your answers, you're experiencing significant caregiver burnout. The good news: You're exactly who OncoDiary is designed for.\n\nYou don't need to fix everything at once. You need one consistent place to begin rebuilding—and that's what OncoDiary provides.\n\n**What caregivers like you have experienced:**\n• Reclaimed permission to prioritize your own healing\n• Sleep finally returning and quality of life improving\n• Real frameworks that shift how you understand caregiving\n• Practical tools that actually work for a dysregulated nervous system\n• Moving from white-knuckling through days to presence and patience",
        image: "/images/placeholder-caregiver-support.webp"
      },
      male_caregiver: {
        message: "Your answers show the real burden of caregiving. You don't have to carry it alone, and you don't have to feel broken for struggling.\n\nOncoDiary is built for caregivers like you—who need practical, evidence-based tools, not empty platitudes.\n\n**What caregivers like you report:**\n• Understanding that nervous system changes are biology, not weakness\n• Direct frameworks that don't minimize what caregiving actually is\n• Specific tools for the anxiety that comes with supporting someone through cancer\n• Moving from crisis mode (3-hour sleep, hypervigilance) to actual healing\n• Reclaiming your role as partner instead of just nurse",
        image: "/images/placeholder-man-recovery.webp"
      }
    },
    nextPage: "page23_heard_from_professional"
  },

  page23_heard_from_professional: {
    pageNum: 29,
    type: "question",
    singleSelect: true,
    question: "Did a psycho-oncologist or oncology team member recommend OncoDiary?",
    emoji: "👂",
    options: [
      { label: "✅ Yes", value: "yes" },
      { label: "❌ No", value: "no" }
    ],
    nextPage: "page24_upsell"
  },

  page24_upsell: {
    pageNum: 30,
    type: "upsell",
    singleSelect: false,
    heading: "Personalized Offer"
  }
};
